import { REPUB_COUNTRY, type RequestContext } from '@cx-web/domain-types';
import { featureFlags } from '@cx-web/feature-flags/provider';
import { CATEGORY_ORDERING_CONFIGS, REPUB_CONFIG, TRENDS_CONFIGS } from '@cx-web/repub-config';
import { getRepubMetaFromRepub, isOcPlusRepub } from '@cx-web/utils';
import { Progress } from '@fpsm/cxy-components/Progress';
import { TabsList, TabsTrigger } from '@fpsm/cxy-components/Tabs';
import memoize from 'lodash.memoize';

import { REPUBS } from '@cx-modules/constants';

import { BetButton } from '~/components/bet-button/BetButton.tsx';
import { BetTypeLabel } from '~/components/BettingInsights/components/BetTypeLabels/BetTypeLabels.tsx';
import { CheckboxFilter } from '~/components/BettingInsights/components/Filters/CheckboxFilter';
import { DateRefinement } from '~/components/BettingInsights/components/Filters/DateRefinement.tsx';
import { SliderFilter } from '~/components/BettingInsights/components/Filters/SliderFilter';
import { Profile } from '~/components/BettingInsights/components/Profile';
import { Autocomplete } from '~/components/BettingInsights/components/Search/Autocomplete.tsx';
import { styles, Trends } from '~/components/BettingInsights/components/Trends';
import {
  trackBetTypeFilterChange,
  trackLastGamesTabClick,
  trackTrendsBetClick,
} from '~/components/BettingInsights/tracking';
import { isGzRepub, type MarketDisplayNames } from '~/components/BettingInsights/types';
import { type BettingInsightsConfigFactoryOptions, type TrendsIndex } from '~/configuration/types';
import { type BettingInsightsUIConfig, createBettingInsightsUIConfig } from '~/configuration/ui/BettingInsightsUI';
import { formatDate } from '~/configuration/utils/formatDate.ts';
import { formatLine } from '~/configuration/utils/formatLine';
import { hasMinBookmakersForRepub } from '~/configuration/utils/hasMinBookmakersForRepub';
import { FILTERS_CONFIGS } from '~/search-domain/config/filters-config';
import { EVENT_DISPLAY_NAME, getEventDisplayNames } from '~/search-domain/constants/eventDisplayNames';
import { getTimezoneForRepub } from '~/search-domain/constants/timezones';
import { trendsIndex } from '~/search-domain/indexes.ts';
import { getMatchupTeamInfo } from '~/utils/getMatchupTeamInfo';

export function createTrendsUIConfig(
  requestContext: RequestContext,
  options: BettingInsightsConfigFactoryOptions = {},
  tags: string[],
) {
  const { geolocationInfo, usageContext = 'default' } = options;

  const filterTranslations = FILTERS_CONFIGS[requestContext.repubConfig.name as keyof typeof FILTERS_CONFIGS];
  const trendTranslations = TRENDS_CONFIGS[requestContext.repubConfig.name as keyof typeof TRENDS_CONFIGS];

  const timezone = getTimezoneForRepub(requestContext.repubConfig.name);
  const dateAttribute = timezone ? 'subeventStartDateLocal' : 'subeventStartDate';

  const { repubCountry: factoryRepubCountry, repubIs: factoryRepubIs } = getRepubMetaFromRepub(
    requestContext.repubConfig.name,
  );

  const uiConfig: BettingInsightsUIConfig<TrendsIndex, any> = {
    card: {
      styles,
      table: {
        body: {
          render(_context, { bookmakers, content, label, resultIndex, styles, table }) {
            return (
              <Trends.Body
                bookmakers={bookmakers}
                content={content}
                label={label}
                resultIndex={resultIndex}
                styles={styles}
                table={table}
              />
            );
          },
        },
        getContent(_context, { result }) {
          return [result];
        },
        footer: {
          render(context, { content, resourcesUrl }) {
            const formatDateToTimezone = (date: string, format?: string) => {
              return formatDate(date, format, requestContext.repubConfig);
            };

            const repub = context.repubConfig.name;
            const repubCountry = isOcPlusRepub(repub) ? REPUB_COUNTRY[repub] : REPUB_COUNTRY.OC;
            return (
              <Trends.Footer
                content={content}
                formatDate={formatDateToTimezone}
                repubCountry={repubCountry}
                repubIs={factoryRepubIs}
                resourcesUrl={resourcesUrl}
              />
            );
          },
        },
        headings: {
          row: 3,
          content: [
            {
              key: 'trends',
              render() {
                return <span>Trends</span>;
              },
              isMain: true,
              // `sr-only` (Tailwind), not cxy's `isVisuallyHidden`: that class only reached this
              // bundle because cxy's global reset was being injected into consumers, which
              // NGF-7035 stops. The app already uses `sr-only` elsewhere.
              className: 'sr-only',
              justifyContent: 'center',
              mobileView: 'hide',
            },
            {
              key: 'bestOdds',
              render() {
                return filterTranslations.bestOdds;
              },
              mobileView: 'hide',
              justifyContent: 'center',
            },
          ],
        },
        main: {
          render(context, { content, resourcesUrl }) {
            const repub = requestContext.repubConfig.name;
            if (!isOcPlusRepub(repub)) {
              return null;
            }

            const q4PropsEnabled =
              context.featureFlags.find(flag => flag.name === featureFlags.SUBS__PLUS__Q4_PROPS)?.enabled ?? false;

            const repubCountry = REPUB_COUNTRY[repub];
            const betName = content.betNameMap?.[repubCountry] ?? content.betName;

            return (
              <Profile
                aiProjection={content.aiProjection}
                betName={betName}
                line={content.line}
                marketClassification={content.marketClassification}
                marketDisplayName={content.marketDisplayNameMap[repubCountry] ?? content.marketDisplayNameMap.us}
                marketPropType={content.marketPropType}
                repubCountry={repubCountry}
                resourcesUrl={resourcesUrl}
                sportsData={content.sportsData}
                teamData={content.teamData}
                {...(q4PropsEnabled && { type: content.type })}
              />
            );
          },
        },
        cells: [
          {
            key: 'trends',
            isFullWidthMobile: true,
            isMain: true,
            className: 'tabs',
            order: 2,
            render(context, { content, resultIndex: _ }) {
              const last5 = `${content.betId}-progress-tab-last5`;
              const last10 = `${content.betId}-progress-tab-last10`;
              const last20 = `${content.betId}-progress-tab-last20`;

              const disabledTabs = {
                last5: !content.sportsData.history || content.sportsData.history.length === 0,
                last10: !content.sportsData.history || content.sportsData.history.length <= 5,
                last20: !content.sportsData.history || content.sportsData.history.length <= 10,
              };

              const repub = context.repubConfig.name;
              const isGZ = isGzRepub(repub);
              const pStyle = isGZ ? { fontSize: 'var(--cxy-fontSize-xxs)' } : {};

              return (
                <TabsList>
                  <TabsTrigger
                    data-testid="last5"
                    disabled={disabledTabs.last5}
                    onClick={() => trackLastGamesTabClick({ selection: trendTranslations.last5 })}
                    value="last5"
                  >
                    <p id={last5} style={pStyle}>
                      {trendTranslations.last5}
                    </p>
                    <Progress
                      aria-labelledby={last5}
                      disabled={disabledTabs.last5}
                      value={
                        content.sportsData.trends?.last5.hitRate
                          ? +content.sportsData.trends.last5.hitRate.toFixed()
                          : 0
                      }
                    ></Progress>
                  </TabsTrigger>
                  <TabsTrigger
                    data-testid="last10"
                    disabled={disabledTabs.last10}
                    onClick={() => trackLastGamesTabClick({ selection: trendTranslations.last10 })}
                    value="last10"
                  >
                    <p id={last10} style={pStyle}>
                      {trendTranslations.last10}
                    </p>
                    <Progress
                      aria-labelledby={last10}
                      disabled={disabledTabs.last10}
                      value={
                        content.sportsData.trends?.last10.hitRate
                          ? +content.sportsData.trends.last10.hitRate.toFixed()
                          : 0
                      }
                    ></Progress>
                  </TabsTrigger>
                  <TabsTrigger
                    data-testid="last20"
                    disabled={disabledTabs.last20}
                    onClick={() => trackLastGamesTabClick({ selection: trendTranslations.last20 })}
                    value="last20"
                  >
                    <p id={last20} style={pStyle}>
                      {trendTranslations.last20}
                    </p>
                    <Progress
                      aria-labelledby={last20}
                      disabled={disabledTabs.last20}
                      value={
                        content.sportsData.trends?.last20.hitRate
                          ? +content.sportsData.trends.last20.hitRate.toFixed()
                          : 0
                      }
                    ></Progress>
                  </TabsTrigger>
                </TabsList>
              );
            },
          },
          {
            key: 'bestOdds',
            render(_context, { bookmakers, content }) {
              const repub = requestContext.repubConfig.name;
              if (!isOcPlusRepub(repub)) {
                return null;
              }

              const repubCountry = REPUB_COUNTRY[repub];
              const marketDisplayName = content.marketDisplayNameMap[repubCountry] ?? content.marketDisplayNameMap.us;

              const bookmaker = bookmakers[content.bookmakerCode];
              if (!bookmaker) {
                return null;
              }

              const hasMinBookmakers = hasMinBookmakersForRepub(repub, content.bookmakersCount);

              // We need to add IE to the countries in CX Modules in a follow up MR
              const isUkOrIe = geolocationInfo?.countryCode === 'GB';

              const isBetDisabled = (repub === REPUBS.WS.name && !isUkOrIe) || !hasMinBookmakers;

              const hideBookieLogo = repub === REPUBS.WS.name || repub === REPUBS.GZ.name || isBetDisabled;

              const onClick = memoize(() => {
                trackTrendsBetClick({
                  bet: content,
                  bookieName: bookmaker.bookmakerName,
                  component: 'Trends',
                });
              });

              return (
                <BetButton
                  aria-label={`Place a bet on ${content.betName} ${marketDisplayName}
                                        with ${bookmaker.bookmakerName} at odds of ${content.oddsAmerican}`}
                  bet={content}
                  bookieColour={bookmaker.brandColour || 'black'}
                  bookieLogo={bookmaker.logoSmall}
                  bookieName={bookmaker.bookmakerName}
                  disabled={isBetDisabled}
                  flexDirection="row"
                  hideBookieLogo={hideBookieLogo}
                  line={content.line ? formatLine(content.line) : undefined}
                  onClick={onClick}
                  shouldHideLabelOnDesktop
                  unformattedOdds={content}
                />
              );
            },
            isFullWidthMobile: true,
            justifyContent: 'center',
          },
        ],
      },
      title: {
        format:
          usageContext === 'match-centre-tab'
            ? ''
            : ({ result }) => {
                const { categoryGroupName, subeventName, teamData } = result;
                const { matchupString } = getMatchupTeamInfo({ categoryGroupName, subeventName, teamData });

                const countryCode =
                  requestContext.repubConfig.countryCode.toLocaleLowerCase() as keyof MarketDisplayNames;
                const eventDisplayName = result.eventDisplayNameMap?.[countryCode] || '{eventDisplayName}';
                return `${eventDisplayName} | ${matchupString} | {subeventStartTime}`;
              },
        getResultOverrides(_context, { result }) {
          return {
            subeventStartTime: formatDate(result.subeventStartTime, undefined, requestContext.repubConfig),
          };
        },
      },
      wrapper: (_context, { children, config, content, result }) => (
        <Trends config={config} content={content} result={result}>
          {children}
        </Trends>
      ),
    },
    initialUiState(context) {
      const plusConfig = REPUB_CONFIG[context.repubConfig.name]?.plus;
      return {
        hitsPerPage: 10,
        range: {
          'sportsData.hitRates.last10': '60:100',
        },
        refinementList: {
          eventDisplayName: [plusConfig?.trends.defaultEventDisplayName ?? 'EPL'],
        },
      };
    },
    refinements: {
      clearAll: {
        excludedAttributes: ['sportsData.hitRates.last10'],
      },
      components: [
        {
          attribute: 'bookmakerCode',
          getLabel(_context) {
            return 'Bookmaker (Internal use only)';
          },
          render(context, { bookmakers }) {
            const areInternalSearchToolsEnabled =
              context.featureFlags.find(flag => flag.name === featureFlags.SUBS__PLUS__INTERNAL_SEARCH_TOOLS)
                ?.enabled ?? false;

            if (!areInternalSearchToolsEnabled) {
              return null;
            }

            return (
              <CheckboxFilter
                attribute={context.attribute}
                isDisabled={context.isWalled}
                label="Bookmaker (Internal use only)"
                repubCountry={factoryRepubCountry}
                sortBy={['name:asc']}
                transformFieldLabel={({ count, label }) => {
                  const bookmaker = bookmakers[label];
                  return `${bookmaker?.bookmakerName ?? label} (${count})`;
                }}
              />
            );
          },
        },
        {
          attribute: 'sportsData.hitRates.last10',
          getLabel(_context) {
            return filterTranslations.minimumHitRate;
          },
          render(context) {
            return (
              <SliderFilter
                attribute={context.attribute}
                data-analytics="minimumHitRate"
                isDisabled={context.isWalled}
                label={filterTranslations.minimumHitRate}
                max={100}
                step={10}
              />
            );
          },
        },
        {
          // Use subeventStartDateLocal for GZ (local timezone), subeventStartDate for others (UTC)
          attribute: dateAttribute as 'subeventStartDate' | 'subeventStartDateLocal',
          getLabel(_context) {
            return filterTranslations.date;
          },
          render(context) {
            const isMatchCentreTab = usageContext === 'match-centre-tab';
            if (isMatchCentreTab) return null;

            return (
              <DateRefinement
                attribute={context.attribute}
                isDisabled={context.isWalled}
                label={filterTranslations.date}
                repubCountry={factoryRepubCountry}
              />
            );
          },
        },
        {
          attribute: 'marketPropType',
          getLabel(_context) {
            return filterTranslations.propsFilter;
          },
          render(context) {
            const q4PropsEnabled =
              context.featureFlags.find(flag => flag.name === featureFlags.SUBS__PLUS__Q4_PROPS)?.enabled ?? false;

            const label = filterTranslations.propsFilter;

            return (
              <CheckboxFilter
                attribute={context.attribute}
                isDisabled={context.isWalled}
                label={label}
                repubCountry={factoryRepubCountry}
                sortBy={['name:asc']}
                trackChange={value => {
                  trackBetTypeFilterChange({ selection: value });
                }}
                transformFieldLabel={({ label: fieldLabel }) =>
                  fieldLabel.replace('Props', filterTranslations.propsFilter)
                }
                {...(q4PropsEnabled && { childrenPlacement: 'before' })}
              >
                {q4PropsEnabled && (
                  <CheckboxFilter
                    attribute="type"
                    isDisabled={context.isWalled}
                    repubCountry={factoryRepubCountry}
                    showMore={false}
                    trackChange={value => {
                      trackBetTypeFilterChange({ selection: value });
                    }}
                    transformFieldLabel={({ label }) => <BetTypeLabel size="small" type={label} />}
                  />
                )}
              </CheckboxFilter>
            );
          },
        },
      ],
    },
    results: {
      categories(context) {
        const isMatchCentreTab = usageContext === 'match-centre-tab';
        if (isMatchCentreTab) return null;

        const repubName = context.repubConfig.name;
        const categoryOrdering =
          CATEGORY_ORDERING_CONFIGS[repubName as keyof typeof CATEGORY_ORDERING_CONFIGS] ??
          CATEGORY_ORDERING_CONFIGS.OC;

        return {
          attribute: EVENT_DISPLAY_NAME,
          labels: getEventDisplayNames(repubName),
          order: categoryOrdering,
        };
      },
      labelledBy: '#bet-name',
      propsName: 'bets',
    },
    search: {
      render(context, { searchAttributes }) {
        const textSearchEnabled =
          context.featureFlags.find(f => f.name === featureFlags.SUBS__PLUS__TEXT_SEARCH)?.enabled ?? false;

        if (!textSearchEnabled) {
          return null;
        }

        return (
          <Autocomplete
            disabled={context.isWalled}
            indexName={trendsIndex}
            repub={context.repubConfig.name}
            searchAttributes={searchAttributes}
          />
        );
      },
    },
    sorting: {
      canSort() {
        return true;
      },
    },
  };

  // Return frozen config object directly (merged from config.ts)
  return { ui: createBettingInsightsUIConfig(requestContext, uiConfig, tags) };
}
